/**
 * The CBD Hut — header surface controller
 * Owns desktop mega-menu behaviour and coordinates mega menu, mobile drawer
 * and predictive search so only one header surface can be open at a time.
 */
(() => {
  'use strict';

  if (window.__CBH_HEADER_SURFACES_V4__) return;
  window.__CBH_HEADER_SURFACES_V4__ = true;

  const ROOT = document.documentElement;
  const HEADER_SELECTOR = '.section-header';
  const MEGA_SELECTOR = 'details[data-cbh-mega]';
  const DRAWER_SELECTOR = 'details.menu-drawer-container';
  const SEARCH_SELECTOR = 'cbh-predictive-search';
  const ACCOUNT_SELECTOR = 'shopify-account';
  const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
  const boundMegas = new WeakSet();
  const timers = new WeakMap();

  const sectionFor = (node) => node?.closest?.(HEADER_SELECTOR) || null;
  const summaryFor = (details) => details?.querySelector?.(':scope > summary') || null;

  function setExpanded(details) {
    const summary = summaryFor(details);
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }

  function closeMega(details, restoreFocus = false) {
    if (!details?.open) return;
    details.open = false;
    details.removeAttribute('open');
    setExpanded(details);
    if (restoreFocus) summaryFor(details)?.focus({ preventScroll: true });
  }

  function closeMegas(section, except = null) {
    section?.querySelectorAll(`${MEGA_SELECTOR}[open]`).forEach((details) => {
      if (details !== except) closeMega(details);
    });
  }

  function closeSearch(section) {
    section?.querySelectorAll(SEARCH_SELECTOR).forEach((search) => {
      if (typeof search.close === 'function') {
        search.close({ restoreFocus: false });
      } else {
        search.removeAttribute('open');
      }
    });
    document.dispatchEvent(new CustomEvent('cbh:predictive-search:request-close'));
  }

  function closeDrawer(section) {
    const details = section?.querySelector(`${DRAWER_SELECTOR}[open]`);
    if (!details) return;
    const host = details.closest('header-drawer');
    const summary = summaryFor(details);
    if (host && typeof host.closeMenuDrawer === 'function') {
      host.closeMenuDrawer(null, summary);
    } else {
      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }
  }

  function openMega(details, focusFirst = false) {
    const section = sectionFor(details);
    if (!section || !details) return;
    closeDrawer(section);
    closeSearch(section);
    closeMegas(section, details);
    details.open = true;
    details.setAttribute('open', '');
    setExpanded(details);
    syncState(section);

    if (focusFirst) {
      requestAnimationFrame(() => {
        details.querySelector('[data-cbh-mega-panel] a[href]')?.focus();
      });
    }
  }

  function clearTimers(details) {
    const state = timers.get(details);
    if (!state) return;
    window.clearTimeout(state.open);
    window.clearTimeout(state.close);
    timers.delete(details);
  }

  function schedule(details, type, delay, callback) {
    const state = timers.get(details) || { open: 0, close: 0 };
    window.clearTimeout(state[type]);
    state[type] = window.setTimeout(callback, delay);
    timers.set(details, state);
  }

  function bindMega(details) {
    if (!details || boundMegas.has(details)) return;
    boundMegas.add(details);

    const summary = summaryFor(details);
    const panel = details.querySelector('[data-cbh-mega-panel]');
    if (!summary || !panel) return;

    details.addEventListener('toggle', () => {
      setExpanded(details);
      const section = sectionFor(details);
      if (details.open) {
        closeDrawer(section);
        closeSearch(section);
        closeMegas(section, details);
      }
      syncState(section);
    });

    summary.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMega(details, true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMega(details, true);
        syncState(sectionFor(details));
      }
    });

    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closeMega(details, true);
      syncState(sectionFor(details));
    });

    if (HOVER_QUERY.matches) {
      summary.addEventListener('pointerenter', () => {
        clearTimers(details);
        schedule(details, 'open', 80, () => openMega(details));
      });

      details.addEventListener('pointerenter', () => {
        const state = timers.get(details);
        if (state?.close) window.clearTimeout(state.close);
      });

      details.addEventListener('pointerleave', (event) => {
        if (event.relatedTarget && details.contains(event.relatedTarget)) return;
        clearTimers(details);
        schedule(details, 'close', 150, () => {
          closeMega(details);
          syncState(sectionFor(details));
        });
      });
    }
  }

  function surfaceState(section) {
    if (!section) return { mega: false, drawer: false, search: false };
    return {
      mega: !!section.querySelector(`${MEGA_SELECTOR}[open]`),
      drawer: !!section.querySelector(`${DRAWER_SELECTOR}[open]`),
      search: Array.from(section.querySelectorAll(SEARCH_SELECTOR)).some(
        (search) => search.hasAttribute('open') || search.isOpen === true
      ),
    };
  }

  function syncState(section = null) {
    const sections = section ? [section] : Array.from(document.querySelectorAll(HEADER_SELECTOR));
    let drawerOpen = false;
    let searchOpen = false;

    sections.forEach((item) => {
      const state = surfaceState(item);
      const anyOpen = state.mega || state.drawer || state.search;
      item.classList.toggle('menu-open', state.drawer);
      item.classList.toggle('cbh-overlay-open', anyOpen);
      drawerOpen ||= state.drawer;
      searchOpen ||= state.search;
    });

    if (!section) {
      ROOT.classList.toggle('cbh-drawer-open', drawerOpen);
      ROOT.classList.toggle('cbh-search-open', searchOpen);
      document.body?.classList.toggle('predictive-search-open', searchOpen);
    } else {
      const allStates = Array.from(document.querySelectorAll(HEADER_SELECTOR)).map(surfaceState);
      ROOT.classList.toggle('cbh-drawer-open', allStates.some((state) => state.drawer));
      ROOT.classList.toggle('cbh-search-open', allStates.some((state) => state.search));
      document.body?.classList.toggle('predictive-search-open', allStates.some((state) => state.search));
    }
  }

  function init(root = document) {
    root.querySelectorAll?.(MEGA_SELECTOR).forEach(bindMega);
    syncState();
  }

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details?.matches?.(DRAWER_SELECTOR)) return;
      const section = sectionFor(details);
      if (details.open) {
        closeMegas(section);
        closeSearch(section);
      }
      requestAnimationFrame(() => syncState(section));
    },
    true
  );

  document.addEventListener('cbh:predictive-search:open', (event) => {
    const section = sectionFor(event.target);
    closeMegas(section);
    closeDrawer(section);
    requestAnimationFrame(() => syncState(section));
  });

  document.addEventListener('cbh:predictive-search:close', (event) => {
    requestAnimationFrame(() => syncState(sectionFor(event.target)));
  });

  // Shopify's account `open` event is composed but does not bubble, so listen in capture.
  // Keep the account sheet aligned to its actual trigger and make it mutually exclusive
  // with the theme-owned mega menu, mobile drawer and predictive-search surfaces.
  document.addEventListener(
    'open',
    (event) => {
      const account = event.target;
      if (!(account instanceof HTMLElement) || !account.matches(ACCOUNT_SELECTOR)) return;

      const section = sectionFor(account);
      closeMegas(section);
      closeDrawer(section);
      closeSearch(section);

      const bottom = Math.max(0, Math.round(account.getBoundingClientRect().bottom));
      account.style.setProperty('--shopify-account-dialog-position-top', `${bottom}px`);
      requestAnimationFrame(() => syncState(section));
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      const account = event.target;
      if (!(account instanceof HTMLElement) || !account.matches(ACCOUNT_SELECTOR)) return;
      requestAnimationFrame(() => syncState(sectionFor(account)));
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (typeof event.button === 'number' && event.button !== 0) return;
      document.querySelectorAll(`${MEGA_SELECTOR}[open]`).forEach((details) => {
        if (!details.contains(event.target)) closeMega(details);
      });
      syncState();
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    document.querySelectorAll(`${MEGA_SELECTOR}[open]`).forEach((details) => {
      if (!details.contains(event.target)) closeMega(details);
    });
    syncState();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target?.closest?.(MEGA_SELECTOR);
    if (details) openMega(details);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const details = event.target?.closest?.(MEGA_SELECTOR);
    if (details) {
      closeMega(details);
      syncState(sectionFor(details));
    }
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
})();
